import { Swiper } from 'swiper';
import { A11y, Navigation, Pagination, Scrollbar, Manipulation } from 'swiper/modules';
import { Fancybox } from '@fancyapps/ui';

/**
 * @class ProductMediaSlider
 * @extends {HTMLElement}
 * @description Create a product media slider, linked to the product variant.
 */
class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();
    // Create elements
    this.buildSlider();
    this.fancybox();
    // Listen for variant change and go to slide
    document.addEventListener('variant-change', (event) => {
      this.slideToVariant(event);
    });
  }

  /*
   * Create main slider
   */
  buildSlider() {
    // Check if Swiper exists
    if (typeof Swiper == 'undefined') {
      return false;
    }

    // Get swiper element
    this.swiperMain = this.querySelector('[data-product-media-slider]');

    // Get initial slide and swiper index
    let initialSlideIndex = 0;
    const initialVariant = parseInt(this.swiperMain.dataset.productMediaSlider);

    if (initialVariant) {
      const initialSlide = this.matchVariantIdWithSlide(initialVariant);
      initialSlideIndex = initialSlide !== false ? initialSlide : initialSlideIndex;
    }

    // Swiper options
    this.swiperMainOptions = {
      modules: [A11y, Navigation, Pagination, Scrollbar, Manipulation],
      threshold: 10,
      direction: 'horizontal',
      initialSlide: initialSlideIndex,
      navigation: {
        nextEl: this.querySelector('[data-swiper="button-next"]'),
        prevEl: this.querySelector('[data-swiper="button-prev"]'),
      },
      pagination: {
        el: this.querySelector('[data-swiper="pagination"]'),
        type: 'bullets',
        clickable: true,
      },
      spaceBetween: 4,
      breakpoints: {
        1024: {
          spaceBetween: 0,
        },
      },
    };

    // Create Main slider
    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

    this.swiperMainInstance.on('activeIndexChange', (event) => {
      this.updatePagination(event.activeIndex + 1);
    });

    // Rebuid Swiper in design mode
    if (Shopify.designMode && this.swiperMainInstance) {
      window.addEventListener('shopify:section:load', () => {
        this.swiperMainInstance.update();
      });
    }
  }

  /*
   * Create fancybox
   */
  fancybox() {
    // Check if Fancybox exists
    if (typeof Swiper == 'undefined') {
      return false;
    }

    Fancybox.bind('[data-fancybox]', {
      animated: false,
      showClass: false,
      mainClass: 'image-zoom',
    });

    const desktopFirstSlide = document.querySelector('[data-desktop-first-slide]');
    if (desktopFirstSlide) {
      desktopFirstSlide.addEventListener('click', (event) => {
        const firstFancybox = this.querySelector('[data-fancybox]');
        firstFancybox.click();
      });
    }
  }

  /*
   * Slide to variant
   * @Param e {object}: event object with variant
   */
  slideToVariant(event) {
    if (event?.detail?.variant?.id) {
      const slideIndex = this.matchVariantIdWithSlide(event.detail.variant.id);
      if (typeof slideIndex == 'number') {
        this.goToSlide(slideIndex);
      }
    }
  }

  /*
   * Match variant id with slide
   * @param id {int}: variant id
   */
  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMain) {
      return false;
    }

    // Get slides
    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    // Match id with slides id's
    const slide = [...slides].find((slide) => {
      // Get variant id's attached to slide
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);

      // Check if variant id match the slide variant id's
      if (slideVariants.includes(id)) {
        return slide;
      }
    });

    // return false if no slide found
    if (!slide) {
      return false;
    }

    // Get the DOM index
    let slideIndex = getNodeIndex(slide);

    // On desktop (lg breakpoint), the first slide is hidden with lg:hidden class
    // Swiper counts hidden slides in DOM but not in its active index
    // So we need to adjust the index for desktop by subtracting 1 if it's not the first slide
    const isDesktop = window.innerWidth >= 1024;
    const isFirstSlide = slideIndex === 0;

    if (isDesktop && !isFirstSlide) {
      slideIndex = slideIndex - 1;
    }

    return slideIndex;
  }

  /*
   * go to slide
   * @param slide {node} / {int}: slide node / index
   */
  goToSlide(slide) {
    if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) {
      return false;
    }

    // Get index, transform node to slide index or string to int
    const index = typeof slide == 'number' ? parseInt(slide) : getNodeIndex(slide);

    // go to slide
    this.swiperMainInstance.slideTo(index);
  }

  /**
   * updatePagination
   * @param {Number} amount
   */
  updatePagination(amount) {
    this.querySelector('[data-swiper="active-index"]').textContent = amount;
  }
}

if (!customElements.get('product-media-slider')) {
  window.ProductMediaSlider = ProductMediaSlider;
  customElements.define('product-media-slider', ProductMediaSlider);
}
